import React, { useState, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { 
  Star, 
  Quote,
  Heart,
  Users,
  Shield,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Award
} from 'lucide-react';
import '../../../assets/css/home/Testimonials.css';

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const sectionRef = useRef(null);

  const testimonials = [
    {
      id: 1,
      name: 'Ahmed Hassan',
      role: 'Donor',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed',
      rating: 5,
      text: 'SDMS made donating so easy! I can track exactly where my donations go and see the real impact. The transparency is incredible and gives me confidence that my contributions are making a difference.',
      donation: '₨ 250,000 donated',
      color: '#FFB800',
      icon: Heart
    },
    {
      id: 2,
      name: 'Fatima Malik',
      role: 'Receiver',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Fatima',
      rating: 5,
      text: 'When my family needed urgent medical support, SDMS connected us with verified donors within hours. The system is fast, secure, and truly life-changing. Forever grateful!',
      donation: 'Received ₨ 150,000',
      color: '#1F7A7A',
      icon: Users
    },
    {
      id: 3,
      name: 'Dr. Khalid Ibrahim',
      role: 'NGO Representative',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Khalid',
      rating: 5,
      text: 'As an NGO, SDMS has revolutionized how we manage donations. The platform is efficient, transparent, and helps us reach more people in need. Highly recommended for all charitable organizations!',
      donation: '500+ beneficiaries',
      color: '#165C5C',
      icon: Shield
    },
    {
      id: 4,
      name: 'Sara Ali',
      role: 'Donor',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sara',
      rating: 5,
      text: 'The verification process gives me peace of mind. I know my donations are reaching genuine people who need help. The impact reports are detailed and heartwarming.',
      donation: '₨ 180,000 donated',
      color: '#FFB800',
      icon: Heart
    },
    {
      id: 5,
      name: 'Hassan Raza',
      role: 'Receiver',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Hassan',
      rating: 5,
      text: 'SDMS helped me get education support for my children when I lost my job. The process was dignified and respectful. Thank you to all the generous donors on this platform!',
      donation: 'Education support received',
      color: '#1F7A7A',
      icon: Users
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={18}
        fill={index < rating ? '#FFB800' : 'none'}
        stroke={index < rating ? '#FFB800' : '#E9ECEF'}
        className="star-icon"
      />
    ));
  };

  const nextTestimonial = () => setActiveIndex((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (activeIndex + i + testimonials.length) % testimonials.length;
      visible.push({ ...testimonials[index], position: i });
    }
    return visible;
  };

  return (
    <section className="testimonials-section" ref={sectionRef}>

      <div
        className="animated-background"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(31,122,122,0.12) 0%, transparent 55%)`
        }}
      />

      <div className="floating-shape shape-1"></div>
      <div className="floating-shape shape-2"></div>
      <div className="floating-shape shape-3"></div>
      <div className="floating-shape shape-4"></div>

      <Container>

        {/* Header */}
        <div className="testimonials-header">

          {/* Badge — block level, neeche title aaye */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
            <div className="section-badge">
              <Sparkles size={18} />
              <span>TESTIMONIALS</span>
            </div>
          </div>

          {/* ✅ Title — apni line pe, gradient working */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <span
              className="section-title"
              style={{
                background: 'linear-gradient(135deg, #165C5C 0%, #1F7A7A 55%, #FFB800 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: 'inline-block',
                fontSize: '48px',
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              What Our Community Says
            </span>
          </div>

          <p className="section-subtitle">
            Real stories from donors, receivers, and NGOs who trust SDMS
          </p>
        </div>

        {/* Carousel */}
        <div className="testimonials-carousel">
          <div className="carousel-track">
            {getVisibleTestimonials().map((testimonial) => {
              const Icon = testimonial.icon;
              const isCenter = testimonial.position === 0;
              const isLeft   = testimonial.position === -1;
              const isRight  = testimonial.position === 1;

              return (
                <div
                  key={testimonial.id}
                  className={`testimonial-card
                    ${isCenter ? 'center' : ''}
                    ${isLeft   ? 'left'   : ''}
                    ${isRight  ? 'right'  : ''}
                    ${hoveredCard === testimonial.id ? 'hovered' : ''}
                  `}
                  onMouseEnter={() => setHoveredCard(testimonial.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{ '--card-color': testimonial.color }}
                >
                  <div className="card-glow"></div>

                  <div className="quote-icon">
                    <Quote size={38} />
                  </div>

                  <div
                    className="category-badge"
                    style={{
                      backgroundColor: `${testimonial.color}18`,
                      color: testimonial.color,
                      borderColor: testimonial.color
                    }}
                  >
                    <Icon size={15} />
                    <span>{testimonial.role}</span>
                  </div>

                  <div className="avatar-container">
                    <div className="avatar-ring" style={{ borderColor: testimonial.color }}>
                      <img src={testimonial.image} alt={testimonial.name} />
                    </div>
                    <div className="verified-badge">
                      <Award size={15} fill={testimonial.color} stroke="#FFF" />
                    </div>
                  </div>

                  <h4 className="testimonial-name">{testimonial.name}</h4>

                  <div className="testimonial-rating">
                    {renderStars(testimonial.rating)}
                  </div>

                  <p className="testimonial-text">{testimonial.text}</p>

                  <div className="donation-info">
                    <Sparkles size={13} />
                    <span>{testimonial.donation}</span>
                  </div>

                  <div className="sparkle sparkle-1">✦</div>
                  <div className="sparkle sparkle-2">✦</div>
                  <div className="sparkle sparkle-3">✦</div>
                </div>
              );
            })}
          </div>

          <button className="nav-arrow prev" onClick={prevTestimonial} aria-label="Previous">
            <ChevronLeft size={26} />
          </button>
          <button className="nav-arrow next" onClick={nextTestimonial} aria-label="Next">
            <ChevronRight size={26} />
          </button>
        </div>

        {/* Dots */}
        <div className="dots-indicator">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`dot ${activeIndex === index ? 'active' : ''}`}
              onClick={() => setActiveIndex(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* ✅ Stats Row — inline styles so they are ALWAYS visible */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '40px',
          flexWrap: 'wrap',
          marginTop: '20px',
          position: 'relative',
          zIndex: 2,
        }}>

          {[
            { icon: <Heart size={30} color="#FFB800" />, value: '5,000+',  label: 'Happy Donors'      },
            { icon: <Users size={30} color="#FFB800" />, value: '10,000+', label: 'Lives Impacted'    },
            { icon: <Shield size={30} color="#FFB800" />, value: '98%',    label: 'Satisfaction Rate' },
          ].map((stat, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '18px 28px',
                background: '#ffffff',
                border: '2px solid rgba(31,122,122,0.12)',
                borderRadius: '18px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease',
                cursor: 'default',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-6px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {stat.icon}
              <div>
                <div style={{ fontSize: '32px', fontWeight: 800, color: '#165C5C', lineHeight: 1 }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '13px', color: '#6C757D', fontWeight: 600, marginTop: '4px' }}>
                  {stat.label}
                </div>
              </div>
            </div>
          ))}

        </div>

      </Container>
    </section>
  );
};

export default Testimonials;